import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, X, Gift, Award, Clock, Save } from 'lucide-react';
import { useAlert } from '../../contexts/ConfirmContext';

const StampSettings = ({
  settings,
  onSettingsChange,
  onAddStampRule,
  onRemoveStampRule,
  onAddVisitMilestone,
  onRemoveVisitMilestone,
  onSaveResetHours,
  isSaving
}) => {
  const notify = useAlert();
  const [newRule, setNewRule] = useState({
    requiredStamps: 5,
    couponType: '',
    expiryDays: '90'
  });
  const [newMilestone, setNewMilestone] = useState({
    visitCount: 15,
    couponType: '',
    expiryDays: '90'
  });

  // 재적립 제한 시간 입력칸은 settings.stampResetHours(항상 유효한 숫자,
  // 0~24)에 직접 바인딩하지 않고 별도 문자열 상태로 둔다. 직접 바인딩하면
  // 값이 0일 때 지우는 순간 e.target.value가 ''가 되고, 그걸 즉시
  // parseInt해서 NaN -> 0으로 되돌려버려서 화면상 0이 그대로 남아있는
  // 것처럼 보여 "지워지지 않는다"는 문제가 있었다. 입력 중에는 자유롭게
  // 빈 문자열/중간 상태를 허용하고, 포커스를 벗어나거나 저장을 누를
  // 때만 실제 숫자(0~24)로 정리해서 반영한다.
  const [resetHoursInput, setResetHoursInput] = useState(String(settings.stampResetHours));
  useEffect(() => {
    setResetHoursInput(String(settings.stampResetHours));
  }, [settings.stampResetHours]);

  const commitResetHours = () => {
    const parsed = parseInt(resetHoursInput, 10);
    const clamped = Number.isNaN(parsed) ? 0 : Math.min(24, Math.max(0, parsed));
    setResetHoursInput(String(clamped));
    onSettingsChange('stampResetHours', clamped);
    return clamped;
  };

  // 셀렉트 값('30'|'60'|...|'unlimited')을 서버로 보낼 형태(숫자 또는 null)로 바꾼다.
  const expiryDaysToPayload = (value) => (value === 'unlimited' ? null : parseInt(value, 10));

  const addStampRule = async () => {
    if (!newRule.couponType.trim()) {
      await notify('쿠폰 종류를 입력해주세요.');
      return;
    }
    if (!newRule.requiredStamps || newRule.requiredStamps < 1 || newRule.requiredStamps > 10) {
      await notify('필요 스탬프 개수는 1~10 사이로 입력해주세요.');
      return;
    }
    if ((settings.stampRules || []).some((rule) => rule.requiredStamps === newRule.requiredStamps)) {
      await notify('이미 등록된 스탬프 개수입니다.');
      return;
    }

    await onAddStampRule(newRule.requiredStamps, newRule.couponType.trim(), expiryDaysToPayload(newRule.expiryDays));

    setNewRule({
      requiredStamps: 5,
      couponType: '',
      expiryDays: '90'
    });
  };

  const removeStampRule = async (id) => {
    await onRemoveStampRule(id);
  };

  const addVisitMilestone = async () => {
    if (!newMilestone.couponType.trim()) {
      await notify('쿠폰 종류를 입력해주세요.');
      return;
    }
    if (!newMilestone.visitCount || newMilestone.visitCount <= 0) {
      await notify('누적 방문 횟수를 입력해주세요.');
      return;
    }
    if ((settings.visitMilestones || []).some((milestone) => milestone.visitCount === newMilestone.visitCount)) {
      await notify('이미 등록된 방문 횟수입니다.');
      return;
    }

    await onAddVisitMilestone(newMilestone.visitCount, newMilestone.couponType.trim(), expiryDaysToPayload(newMilestone.expiryDays));

    setNewMilestone({
      visitCount: 15,
      couponType: '',
      expiryDays: '90'
    });
  };

  // 목록에 표시할 유효기간 문구
  const formatExpiry = (expiryDays) =>
    expiryDays == null ? '무제한' : `${expiryDays}일`;

  const removeVisitMilestone = async (id) => {
    await onRemoveVisitMilestone(id);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 border-b">
        <h3 className="text-lg font-semibold flex items-center">
          <MessageSquare className="w-5 h-5 mr-2 text-gray-600" />
          스탬프 및 쿠폰 설정
        </h3>
      </div>

      <div className="p-3 space-y-8">
        {/* 재적립 제한 시간 */}
        <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
          <h4 className="text-lg font-medium text-gray-800 mb-1 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-gray-600" />
            재적립 제한 시간
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            같은 손님이 이 시간 안에 다시 적립을 시도하면 막고 안내만 합니다. 0시간은 제한 없음입니다.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              max="24"
              value={resetHoursInput}
              onChange={(e) => setResetHoursInput(e.target.value)}
              onBlur={commitResetHours}
              className="w-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
            />
            <span className="text-gray-700">시간 (0~24)</span>
            <button
              type="button"
              onClick={() => {
                const hours = commitResetHours();
                onSaveResetHours(
                  hours === 0
                    ? '재적립 제한을 없애시겠습니까? (0시간 = 제한 없음)'
                    : `재적립 제한 시간을 ${hours}시간으로 변경하시겠습니까?`,
                  { stampResetHours: hours }
                );
              }}
              disabled={isSaving}
              className="flex items-center gap-1 px-4 py-2 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <Save className="w-4 h-4" />
              저장
            </button>
          </div>
        </div>

        {/* 스탬프 규칙 추가 폼 */}
        <div className="border-2 border-dashed border-slate-300 rounded-xl p-3 bg-gradient-to-br from-slate-50 to-gray-50">
          <h4 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-slate-600" />
            새 스탬프 규칙 추가
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                필요 스탬프 개수
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={newRule.requiredStamps}
                onChange={(e) => setNewRule(prev => ({ ...prev, requiredStamps: parseInt(e.target.value) || '' }))}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
                placeholder="1~10 사이"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                쿠폰 종류
              </label>
              <input
                type="text"
                value={newRule.couponType}
                onChange={(e) => setNewRule(prev => ({ ...prev, couponType: e.target.value }))}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
                placeholder="예: 10% 할인, 무료 음료"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                쿠폰 유효기간
              </label>
              <select
                value={newRule.expiryDays}
                onChange={(e) => setNewRule(prev => ({ ...prev, expiryDays: e.target.value }))}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
              >
                <option value="30">30일</option>
                <option value="60">60일</option>
                <option value="90">90일</option>
                <option value="180">180일</option>
                <option value="360">360일</option>
                <option value="unlimited">무제한</option>
              </select>
            </div>
          </div>

          <button
            onClick={addStampRule}
            className="w-full bg-gradient-to-r from-slate-600 to-slate-700 text-white py-3 px-4 rounded-lg hover:from-slate-700 hover:to-slate-800 transition-all duration-200 font-medium flex items-center justify-center shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            스탬프 규칙 추가하기
          </button>
        </div>

        {/* 방문 마일스톤 추가 폼 */}
        <div className="border-2 border-dashed border-purple-300 rounded-xl p-3 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="mb-4">
            <h4 className="text-lg font-medium text-purple-800 flex items-center">
              <Award className="w-5 h-5 mr-2 text-purple-600" />
              방문 마일스톤 추가
            </h4>
            <p className="text-sm text-purple-600 mt-1 ml-7">
              스탬프 카드 리셋과 무관하게, 누적 방문 횟수가 여기서 설정한 값에 도달하면
              그 방문의 카드 단계 쿠폰 대신 아래 쿠폰을 발급합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">
                누적 방문 횟수
              </label>
              <input
                type="number"
                min="1"
                value={newMilestone.visitCount}
                onChange={(e) => setNewMilestone(prev => ({ ...prev, visitCount: parseInt(e.target.value) || '' }))}
                className="w-full p-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white transition-colors"
                placeholder="예: 15, 20, 100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">
                쿠폰 종류
              </label>
              <input
                type="text"
                value={newMilestone.couponType}
                onChange={(e) => setNewMilestone(prev => ({ ...prev, couponType: e.target.value }))}
                className="w-full p-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white transition-colors"
                placeholder="예: 매직펌, 특별 사은품"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">
                쿠폰 유효기간
              </label>
              <select
                value={newMilestone.expiryDays}
                onChange={(e) => setNewMilestone(prev => ({ ...prev, expiryDays: e.target.value }))}
                className="w-full p-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white transition-colors"
              >
                <option value="30">30일</option>
                <option value="60">60일</option>
                <option value="90">90일</option>
                <option value="180">180일</option>
                <option value="360">360일</option>
                <option value="unlimited">무제한</option>
              </select>
            </div>
          </div>

          <button
            onClick={addVisitMilestone}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 font-medium flex items-center justify-center shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            방문 마일스톤 추가하기
          </button>
        </div>

        {/* 설정된 스탬프 규칙들 */}
        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
            <Gift className="w-5 h-5 mr-2 text-green-600" />
            설정된 스탬프 규칙 ({(settings.stampRules || []).length}개)
          </h4>

          {(settings.stampRules || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {settings.stampRules.map((rule, index) => (
                <div key={rule.id} className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3 relative">
                  <button
                    onClick={() => removeStampRule(rule.id)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>

                  <div className="pr-8">
                    <div className="flex items-center mb-2">
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        규칙 #{index + 1}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 w-20 flex-shrink-0">스탬프:</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-bold">
                          {rule.requiredStamps}개
                        </span>
                      </div>

                      <div className="flex items-start text-sm">
                        <span className="font-medium text-gray-600 w-20 flex-shrink-0 mt-0.5">쿠폰:</span>
                        <span className="text-gray-800 font-medium">{rule.couponType}</span>
                      </div>

                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 w-20 flex-shrink-0">유효기간:</span>
                        <span className="text-gray-700">{formatExpiry(rule.expiryDays)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Gift className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">설정된 스탬프 규칙이 없습니다</p>
            </div>
          )}
        </div>

        {/* 설정된 방문 마일스톤 */}
        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-purple-600" />
            설정된 방문 마일스톤 ({(settings.visitMilestones || []).length}개)
          </h4>

          {(settings.visitMilestones || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {settings.visitMilestones.map((milestone, index) => (
                <div key={milestone.id} className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-3 relative">
                  <button
                    onClick={() => removeVisitMilestone(milestone.id)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>

                  <div className="pr-8">
                    <div className="flex items-center mb-2">
                      <div className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                        마일스톤 #{index + 1}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 w-24 flex-shrink-0">누적 방문:</span>
                        <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-bold">
                          {milestone.visitCount}회
                        </span>
                      </div>

                      <div className="flex items-start text-sm">
                        <span className="font-medium text-gray-600 w-24 flex-shrink-0 mt-0.5">쿠폰:</span>
                        <span className="text-gray-800 font-medium">{milestone.couponType}</span>
                      </div>

                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 w-24 flex-shrink-0">유효기간:</span>
                        <span className="text-gray-700">{formatExpiry(milestone.expiryDays)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">설정된 방문 마일스톤이 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StampSettings;
